import { useState, type ReactNode } from 'react';
import { Card, CardHeader, CardTitle, CardBody, Button } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Receipt, Plus, Pencil, Trash2 } from 'lucide-react';
import { formatDate, formatMoney } from '@/lib/format';
import { useServicePrices, useServiceVatRates, type ServicePrice, type ServiceVatRate } from '@/api/services';
import { useDeleteServicePrice, useDeleteServiceVatRate } from '@/api/services-mutations';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { ServicePriceDialog } from './service-price-dialog';
import { ServiceVatDialog } from './service-vat-dialog';
import { ServiceConfigPricesSection } from './service-config-prices-section';

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;
const rightHeader = (label: string) => () => <span style={{ display: 'block', textAlign: 'right' }}>{label}</span>;
const dash = (v: ReactNode) => (v == null || v === '' ? <span style={muted}>—</span> : v);

/** Ghost icon buttons for a row's Edit / Delete actions. */
function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <span style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-1)' }}>
      <Button size="sm" variant="ghost" aria-label="Edit" onClick={onEdit}><Pencil size={16} /></Button>
      <Button size="sm" variant="ghost" aria-label="Delete" onClick={onDelete}><Trash2 size={16} /></Button>
    </span>
  );
}

/**
 * Prices tab — the service's register/recurring price history and its VAT-rate
 * history, each with add / edit / delete actions. Deletes are confirmed via a
 * destructive `ConfirmDialog`; create / edit use dedicated dialogs.
 */
export function ServicePricesTab({ id, currency = 'VND' }: { id: number; currency?: string }) {
  const toast = useToast();
  const prices = useServicePrices(id);
  const vat = useServiceVatRates(id);
  const deletePrice = useDeleteServicePrice(id);
  const deleteVat = useDeleteServiceVatRate(id);

  const [priceDialog, setPriceDialog] = useState<{ open: boolean; row: ServicePrice | null }>({ open: false, row: null });
  const [vatDialog, setVatDialog] = useState<{ open: boolean; row: ServiceVatRate | null }>({ open: false, row: null });
  const [priceToDelete, setPriceToDelete] = useState<ServicePrice | null>(null);
  const [vatToDelete, setVatToDelete] = useState<ServiceVatRate | null>(null);

  if (prices.isError) return <TabError title="Failed to load prices" onRetry={() => prices.refetch()} />;
  if (prices.isLoading) return <TabLoading rows={4} />;

  const priceRows = prices.data?.results ?? [];
  const vatRows = vat.data?.results ?? [];

  const priceColumns: ColumnDef<ServicePrice, unknown>[] = [
    {
      id: 'price_type',
      header: 'Type',
      cell: ({ row }) => (
        <StatusPill tone={row.original.price_type === 'recurring' ? 'info' : 'neutral'}>
          {row.original.price_type === 'recurring' ? 'Recurring' : 'Register'}
        </StatusPill>
      ),
    },
    { id: 'price', header: rightHeader('Price'), cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(row.original.price, currency)}</span> },
    { id: 'effective_from', header: rightHeader('From'), cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{formatDate(row.original.effective_from)}</span> },
    { id: 'effective_to', header: rightHeader('To'), cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{dash(row.original.effective_to && formatDate(row.original.effective_to))}</span> },
    { id: 'source', header: 'Source', cell: ({ row }) => dash(row.original.source) },
    { id: 'description', header: 'Description', cell: ({ row }) => dash(row.original.description) },
    {
      id: 'actions',
      header: rightHeader('Actions'),
      cell: ({ row }) => (
        <RowActions
          onEdit={() => setPriceDialog({ open: true, row: row.original })}
          onDelete={() => setPriceToDelete(row.original)}
        />
      ),
    },
  ];

  const vatColumns: ColumnDef<ServiceVatRate, unknown>[] = [
    { id: 'source', header: 'Source', cell: ({ row }) => (row.original.source === 'service' ? 'Service' : 'Product group') },
    { id: 'tax_rate', header: rightHeader('Rate'), cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{`${(row.original.tax_rate * 100).toFixed(0)}%`}</span> },
    { id: 'effective_from', header: rightHeader('From'), cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{formatDate(row.original.effective_from)}</span> },
    { id: 'effective_to', header: rightHeader('To'), cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{dash(row.original.effective_to && formatDate(row.original.effective_to))}</span> },
    { id: 'description', header: 'Description', cell: ({ row }) => dash(row.original.description) },
    {
      id: 'actions',
      header: rightHeader('Actions'),
      cell: ({ row }) => (
        <RowActions
          onEdit={() => setVatDialog({ open: true, row: row.original })}
          onDelete={() => setVatToDelete(row.original)}
        />
      ),
    },
  ];

  const confirmDeletePrice = () => {
    if (!priceToDelete) return;
    deletePrice.mutate(priceToDelete.id, {
      onSuccess: () => {
        toast.success('Price deleted');
        setPriceToDelete(null);
      },
      onError: (err) => toast.error('Failed to delete price', { description: err instanceof Error ? err.message : undefined }),
    });
  };

  const confirmDeleteVat = () => {
    if (!vatToDelete) return;
    deleteVat.mutate(vatToDelete.id, {
      onSuccess: () => {
        toast.success('VAT rate deleted');
        setVatToDelete(null);
      },
      onError: (err) => toast.error('Failed to delete VAT rate', { description: err instanceof Error ? err.message : undefined }),
    });
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Card flat>
        <CardHeader
          actions={
            <Button size="sm" variant="secondary" onClick={() => setPriceDialog({ open: true, row: null })}>
              <Plus size={16} /> Add price
            </Button>
          }
        >
          <CardTitle>Prices</CardTitle>
        </CardHeader>
        <CardBody>
          <DataTable
            columns={priceColumns}
            data={priceRows}
            getRowId={(r) => String(r.id)}
            emptyState={<EmptyState icon={<Receipt size={24} />} title="No prices" description="This service has no price history." />}
          />
        </CardBody>
      </Card>

      <Card flat>
        <CardHeader
          actions={
            <Button size="sm" variant="secondary" onClick={() => setVatDialog({ open: true, row: null })}>
              <Plus size={16} /> Add VAT rate
            </Button>
          }
        >
          <CardTitle>VAT rates</CardTitle>
        </CardHeader>
        <CardBody>
          <DataTable
            columns={vatColumns}
            data={vatRows}
            getRowId={(r) => String(r.id)}
            emptyState={<EmptyState icon={<Receipt size={24} />} title="No VAT rates" description="No VAT-rate history for this service." />}
          />
        </CardBody>
      </Card>

      <ServiceConfigPricesSection serviceId={id} currency={currency} />

      <ServicePriceDialog
        serviceId={id}
        open={priceDialog.open}
        price={priceDialog.row}
        onOpenChange={(o) => setPriceDialog((s) => ({ open: o, row: o ? s.row : null }))}
      />
      <ServiceVatDialog
        serviceId={id}
        open={vatDialog.open}
        vat={vatDialog.row}
        onOpenChange={(o) => setVatDialog((s) => ({ open: o, row: o ? s.row : null }))}
      />

      <ConfirmDialog
        open={!!priceToDelete}
        onOpenChange={(o) => (o ? undefined : setPriceToDelete(null))}
        title="Delete price"
        description="This removes the price row. This action cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={deletePrice.isPending}
        onConfirm={confirmDeletePrice}
      />
      <ConfirmDialog
        open={!!vatToDelete}
        onOpenChange={(o) => (o ? undefined : setVatToDelete(null))}
        title="Delete VAT rate"
        description="This removes the VAT-rate row. This action cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={deleteVat.isPending}
        onConfirm={confirmDeleteVat}
      />
    </div>
  );
}
